// Builds the y-axis "views" the SaturationCurveChart switches between (Value / CPA / Marginal ROAS).
// Each view carries its own pre-transformed series plus the formatters and axis label for that
// metric, so the chart stays presentational and just renders whichever view is selected.
//
// For a Volume outcome the efficiency view is CPA (average spend per conversion). For a Revenue
// outcome the efficiency view is MARGINAL ROAS — the slope of the fitted revenue curve
// (Δrevenue/Δspend), i.e. what the next dollar returns — with a break-even line at 1x. We lead with
// marginal rather than average ROAS (revenue / spend) because under diminishing returns the average
// overstates the next-dollar return; marginal + break-even is the decision-relevant "should we
// scale?" read. See scaling-math.marginalPerDollar / breakevenSpend.

import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Brand60 } from '../../../utils/colors';
import { formatAxisSpend } from '../forecasting-constants';
import { ChartSeries, YAxisView } from './SaturationCurveChart';
import { marginalPerDollar } from './scaling-math';
import { ScalingCurvePoint, ScalingOutcomeData, ScalingScatterPoint } from './scalingData';

type YMetric = 'value' | 'cpa' | 'roas';

const ROAS = 'ROAS';

const formatRoas = (value: number): string => `${value.toFixed(2)}x`;
const formatRoasAxis = (value: number): string => `${value.toFixed(1)}x`;

// Transform a fitted curve point into the chosen metric's space.
//   cpa  = spend / revenue — bounds INVERT (lower conversions → higher cost).
//   roas = revenue / spend — AVERAGE ROAS; monotonic in the fit, so bounds keep order
//          (÷ spend), and under diminishing returns this peaks at low spend (why we also
//          offer the marginal view). A zero/non-finite result is skipped by the chart.
// Value passes through unchanged.
const transformCurvePoint = (p: ScalingCurvePoint, metric: YMetric): ScalingCurvePoint => {
  if (metric === 'cpa') {
    return {
      ...p,
      fit: p.fit > 0 ? p.spend / p.fit : NaN,
      lwrPred: p.uprPred > 0 ? p.spend / p.uprPred : NaN,
      uprPred: p.lwrPred > 0 ? p.spend / p.lwrPred : NaN
    };
  }
  if (metric === 'roas') {
    return {
      ...p,
      fit: p.spend > 0 ? p.fit / p.spend : NaN,
      lwrPred: p.spend > 0 ? p.lwrPred / p.spend : NaN,
      uprPred: p.spend > 0 ? p.uprPred / p.spend : NaN
    };
  }
  return p;
};

const transformScatterPoint = (
  pt: ScalingScatterPoint,
  metric: YMetric
): ScalingScatterPoint | null => {
  // Spread so a point keeps its `days` (partial weeks draw hollow).
  if (metric === 'cpa') return pt.volume > 0 ? { ...pt, volume: pt.spend / pt.volume } : null;
  if (metric === 'roas') return pt.spend > 0 ? { ...pt, volume: pt.volume / pt.spend } : null;
  return pt;
};

const buildSeries = (source: ScalingOutcomeData, metric: YMetric): ChartSeries[] => [
  {
    name: metric,
    color: Brand60,
    scatter: source.scatter
      .map(pt => transformScatterPoint(pt, metric))
      .filter((pt): pt is ScalingScatterPoint => pt !== null),
    curve: source.curve.map(p => transformCurvePoint(p, metric))
  }
];

// Marginal ROAS view: the slope of the fitted revenue curve at each spend point (Δrevenue/Δspend)
// plotted as its own curve, so the break-even reference line at 1x reads directly off it. There's no
// prediction band (the slope is a derived quantity, not a fitted value with stored bounds — NaN
// bounds make bandFor skip it) and no scatter (raw weekly points are too noisy to difference).
const buildMarginalRoasSeries = (source: ScalingOutcomeData): ChartSeries[] => [
  {
    name: 'marginalRoas',
    color: Brand60,
    scatter: [],
    curve: source.curve.map((p, i) => ({
      spend: p.spend,
      fit: marginalPerDollar(source.curve, i, source.params) ?? NaN,
      lwrPred: NaN,
      uprPred: NaN,
      extrapolated: p.extrapolated
    }))
  }
];

// Two y-axis views per outcome: the outcome value itself and its attached efficiency metric —
// Volume → CPA (average spend / conversion), Revenue → Marginal ROAS (curve slope). Both are
// derived from the same outcome curve, so no other outcome's data is needed.
export const buildYAxisViews = (outcomeData: ScalingOutcomeData): YAxisView[] => {
  const isRevenue = outcomeData.efficiencyMetric === ROAS;

  const valueView: YAxisView = {
    key: 'value',
    // The current outcome — reads "Volume" or "Revenue".
    label: isRevenue ? 'Revenue' : 'Volume',
    plotType: 'Outcome',
    series: buildSeries(outcomeData, 'value'),
    formatY: isRevenue ? formatMoney : value => formatNumber(value, 0),
    formatAxisY: isRevenue ? formatAxisSpend : value => formatNumber(value, 0)
  };

  // Volume → a single CPA efficiency view (its transformed bounds can balloon, so truncate).
  if (!isRevenue) {
    const cpaView: YAxisView = {
      key: 'cpa',
      label: 'CPA',
      plotType: 'Efficiency',
      series: buildSeries(outcomeData, 'cpa'),
      formatY: formatMoney,
      formatAxisY: formatAxisSpend,
      truncateBand: true
    };
    return [valueView, cpaView];
  }

  // Revenue → BOTH efficiency reads:
  //   ROAS          = average return/$ (revenue/spend); peaks at low spend under diminishing
  //                   returns, band truncated since the transformed bounds can balloon.
  //   Marginal ROAS = the next-$ return (curve slope); the decision-relevant "should we scale?"
  //                   read, with the 1x break-even line. No band (a derived slope).
  const roasView: YAxisView = {
    key: 'roas',
    label: 'ROAS',
    plotType: 'Efficiency',
    series: buildSeries(outcomeData, 'roas'),
    formatY: formatRoas,
    formatAxisY: formatRoasAxis,
    truncateBand: true
  };
  const marginalRoasView: YAxisView = {
    key: 'marginalRoas',
    label: 'Marginal ROAS',
    plotType: 'Marginal',
    series: buildMarginalRoasSeries(outcomeData),
    formatY: formatRoas,
    formatAxisY: formatRoasAxis
  };

  return [valueView, roasView, marginalRoasView];
};
